import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Search, Save, Plus, Edit2, Trash2, RotateCcw,
  DollarSign, Package, Calculator, TrendingUp,
  CheckCircle, AlertCircle, User, MapPin, Lock
} from 'lucide-react';
import apiClient from '../services/api';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

/* ── Debounce ── */
function useDebounce(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

/* ── TypeBadge ── */
const TypeBadge = ({ type }) => {
  const map = {
    LT: { bg: '#DBEAFE', color: '#1D4ED8', label: 'Land Transport' },
    D:  { bg: '#DCFCE7', color: '#15803D', label: 'Domestic' },
    I:  { bg: '#FEF3C7', color: '#B45309', label: 'International' },
  };
  const s = map[type] || { bg: '#f3f4f6', color: '#374151', label: type };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[0.78rem] font-bold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}>{s.label}</span>
  );
};

/* ── Highlight match ── */
function highlightMatch(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>{text.slice(0, idx)}<mark className="bg-yellow-200 text-gray-900 rounded px-px">{text.slice(idx, idx + query.length)}</mark>{text.slice(idx + query.length)}</>
  );
}

/* ── Portal dropdown ── */
const DropdownPortal = ({ anchorRef, children }) => {
  const [pos, setPos] = useState(null);
  const reposition = () => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom, left: r.left, width: r.width });
  };
  useEffect(() => {
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [anchorRef]); // eslint-disable-line
  if (!pos) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 999999 }}>{children}</div>,
    document.body
  );
};

/* ── ConsignmentSearchBox ── */
const ConsignmentSearchBox = ({ onSelect, disabled }) => {
  const [query,       setQuery]     = useState('');
  const [results,     setResults]   = useState([]);
  const [open,        setOpen]      = useState(false);
  const [searching,   setSearching] = useState(false);
  const [highlighted, setHL]        = useState(0);
  const [searchError, setSearchError] = useState('');
  const inputRef       = useRef(null);
  const wrapRef        = useRef(null);
  const listRef        = useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!debouncedQuery.trim()) { setResults([]); setOpen(false); setSearchError(''); return; }
    let cancelled = false;
    (async () => {
      setSearching(true); setSearchError('');
      try {
        const res = await apiClient.get(`/ConsignmentSearch.php?search=${encodeURIComponent(debouncedQuery)}`);
        if (cancelled) return;
        if (res.data.success && res.data.data?.length > 0) { setResults(res.data.data); setHL(0); setOpen(true); }
        else { setResults([]); setOpen(true); }
      } catch { if (!cancelled) { setSearchError('Search failed — check connection'); setOpen(false); } }
      finally { if (!cancelled) setSearching(false); }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  useEffect(() => {
    const handler = (e) => { if (!wrapRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (highlighted >= 0 && listRef.current) listRef.current.querySelectorAll('[data-item]')[highlighted]?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const selectItem  = (item) => { setQuery(item.ConsignmentNo); setOpen(false); setResults([]); onSelect(item); };
  const handleClear = () => { setQuery(''); setResults([]); setOpen(false); setSearchError(''); inputRef.current?.focus(); };
  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHL(h => Math.min(h + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHL(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (results[highlighted]) selectItem(results[highlighted]); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center"
          style={{ color: searching ? PRIMARY : '#9ca3af' }}>
          {searching
            ? <div className="w-4 h-4 border-2 border-[#93C5FD] border-t-transparent rounded-full animate-spin" />
            : <Search size={16} />}
        </div>
        <input ref={inputRef} type="text" value={query}
          onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Type consignment no., shipper or consignee…"
          disabled={disabled} autoFocus
          className={`w-full pl-10 pr-9 py-3 border rounded-lg text-sm bg-white outline-none transition-all duration-150 ${open ? 'border-[#2563EB] shadow-[0_0_0_3px_rgba(37,99,235,0.15)]' : 'border-[#CBD5E1]'}`} />
        {query && (
          <button type="button" onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 bg-transparent border-0 cursor-pointer text-gray-400 hover:text-gray-600 flex items-center">
            <X size={15} />
          </button>
        )}
      </div>

      {open && (
        <DropdownPortal anchorRef={wrapRef}>
          <div ref={listRef} className="bg-white border-2 border-[#2563EB] rounded-xl shadow-2xl max-h-[360px] overflow-y-auto">
            {results.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <Package size={32} className="mx-auto mb-2.5 opacity-25" />
                <div className="text-sm font-semibold">No consignments found</div>
                <div className="text-xs mt-1 text-gray-400">No results for "{debouncedQuery}"</div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center px-3.5 py-2 bg-gray-50 border-b border-gray-200 rounded-t-xl text-[11px] font-bold tracking-widest text-gray-500">
                  <span>{results.length} RESULT{results.length !== 1 ? 'S' : ''}</span>
                  <span className="font-normal text-[11px]">↑↓ navigate · Enter select · Esc close</span>
                </div>
                {results.map((item, idx) => {
                  const isHL  = idx === highlighted;
                  const route = [item.OriginCity || item.OriginCountry, item.DestinationCity || item.DestinationCountry].filter(Boolean).join(' → ');
                  return (
                    <div key={item.ConsignmentID} data-item
                      onMouseEnter={() => setHL(idx)} onClick={() => selectItem(item)}
                      className={`grid gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors duration-75 ${isHL ? 'bg-[#EFF6FF]' : 'bg-white'}`}
                      style={{ gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <TypeBadge type={item.Type} />
                          <span className={`font-mono font-bold text-[0.9rem] ${isHL ? 'text-[#1D4ED8]' : 'text-gray-900'}`}>{highlightMatch(item.ConsignmentNo, query)}</span>
                          {item.ConsignmentDate && <span className="text-[0.76rem] text-gray-400">{new Date(item.ConsignmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                        </div>
                        <div className="flex gap-3.5 text-[0.81rem] text-gray-600 flex-wrap">
                          {(item.ShipperName || item.Shipper)     && <span className="flex items-center gap-0.5"><User size={11} className="opacity-50" />{highlightMatch(item.ShipperName || item.Shipper, query)}</span>}
                          {(item.ConsigneeName || item.Consignee) && <span className="text-gray-500">→ {highlightMatch(item.ConsigneeName || item.Consignee, query)}</span>}
                          {route && <span className="flex items-center gap-0.5 text-gray-400"><MapPin size={11} className="opacity-50" />{route}</span>}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        {item.Status && <span className="px-2 py-0.5 rounded-full text-[0.74rem] font-semibold whitespace-nowrap" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>{item.Status}</span>}
                        {(item.Weight || item.Pieces) && <span className="text-[0.76rem] text-gray-400 whitespace-nowrap">{item.Weight ? `${item.Weight} kg` : ''}{item.Pieces ? ` · ${item.Pieces} pcs` : ''}</span>}
                        {isHL && <span className="text-[0.68rem] font-bold" style={{ color: PRIMARY }}>Enter ↵</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DropdownPortal>
      )}

      {searchError && (
        <div className="flex items-center gap-1 mt-1.5 text-[0.82rem] text-[#DC2626]">
          <AlertCircle size={13} />{searchError}
        </div>
      )}
      <style>{`@keyframes cpf-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* ── SelectedBar ── */
const SelectedBar = ({ data, onClear }) => (
  <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg flex-wrap" style={{ background: '#EFF6FF', border: '2px solid #93C5FD' }}>
    <Package size={15} className="flex-shrink-0" style={{ color: PRIMARY }} />
    <span className="font-mono font-bold text-[0.92rem]" style={{ color: '#1D4ED8' }}>{data.ConsignmentNo}</span>
    <span className="text-[0.82rem] text-gray-700 flex-1 min-w-0 truncate">
      {[data.ShipperName || data.Shipper, data.ConsigneeName || data.Consignee].filter(Boolean).join(' → ')}
    </span>
    <button type="button" onClick={onClear}
      className="flex items-center gap-1 px-3 py-1 bg-white border border-[#CBD5E1] rounded-md text-[0.8rem] font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 flex-shrink-0 transition-colors duration-100">
      <X size={12} /> Change
    </button>
  </div>
);

/* ── Empty form ── */
function getEmptyPaymentForm() {
  return { revenuetypeid: '', Amount: '', TaxPer: 0, SupplierID: '', CostAmount: '', CostTaxPer: 0, Remarks: '' };
}

/* ══════════════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════════════ */
const ConsignmentPaymentWithoutCurrency = () => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [consignmentData, setConsignmentData] = useState(null);
  const [savedPayments,   setSavedPayments]   = useState([]);
  const [localPayments,   setLocalPayments]   = useState([]);

  const [editingPaymentIndex,    setEditingPaymentIndex]    = useState(-1);
  const [isEditingLocal,         setIsEditingLocal]         = useState(false);
  const [editingSavedPaymentId,  setEditingSavedPaymentId]  = useState(null);
  const [paymentForm,            setPaymentForm]            = useState(getEmptyPaymentForm());

  const [headerCustomerID,     setHeaderCustomerID]     = useState('');
  const [headerCustomerLocked, setHeaderCustomerLocked] = useState(false);

  const [customers,    setCustomers]    = useState([]);
  const [revenueTypes, setRevenueTypes] = useState([]);
  const [suppliers,    setSuppliers]    = useState([]);

  const isInvoiced = consignmentData?.PaymentSummary?.IsInvoiced === 1 || consignmentData?.PaymentSummary?.IsInvoiced === '1';

  useEffect(() => {
    (async () => {
      try {
        const [custRes, rtRes, suppRes] = await Promise.all([
          apiClient.get('/Customers.php'),
          apiClient.get('/RevenueType.php'),
          apiClient.get('/Suppliers.php?dropdown'),
        ]);
        setCustomers(Array.isArray(custRes.data?.data) ? custRes.data.data : []);
        const rawRT = Array.isArray(rtRes.data?.data) ? rtRes.data.data : [];
        setRevenueTypes(rawRT.map(rt => ({
          ...rt,
          revenuetypeid:   rt.revenuetypeid   ?? rt.RevenueTypeID  ?? rt.id,
          revenuetypecode: rt.revenuetypecode ?? rt.RevenueTypeCode ?? rt.RevenueCode ?? '',
          revenuetypename: rt.revenuetypename ?? rt.RevenueTypeName ?? rt.RevenueType ?? '',
          isactive:        rt.isactive        ?? rt.IsActive        ?? 1,
        })));
        setSuppliers(Array.isArray(suppRes.data?.data?.suppliers) ? suppRes.data.data.suppliers : Array.isArray(suppRes.data?.data) ? suppRes.data.data : []);
      } catch { setError('Error loading dropdown data'); }
    })();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'F5') { e.preventDefault(); if (localPayments.length > 0 && !isInvoiced) handleSaveAll(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [localPayments, consignmentData, isInvoiced]); // eslint-disable-line

  const handleSelect = async (item) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await apiClient.get(`/ConsignmentPaymentWithoutCurrency.php?awb=${item.ConsignmentNo}`);
      if (res.data.success && res.data.data) {
        const c = res.data.data;
        setConsignmentData(c); setLocalPayments([]);
        loadSavedPayments(c.ConsignmentID);
        if (c.InvoiceToID) { setHeaderCustomerID(String(c.InvoiceToID)); setHeaderCustomerLocked(true); }
        else { setHeaderCustomerID(''); setHeaderCustomerLocked(false); }
      } else { setError('Could not load consignment details'); }
    } catch (err) { setError('Error: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const loadSavedPayments = async (consignmentId) => {
    try {
      const res = await apiClient.get(`/ConsignmentPaymentWithoutCurrency.php?consignmentid=${consignmentId}`);
      if (res.data.success) setSavedPayments(res.data.data || []);
    } catch {}
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({ ...prev, [name]: value }));
  };

  const formAmount    = parseFloat(paymentForm.Amount) || 0;
  const formTaxPer    = parseFloat(paymentForm.TaxPer) || 0;
  const formTaxAmt    = (formAmount * formTaxPer / 100).toFixed(2);
  const formTotalAmt  = (formAmount + parseFloat(formTaxAmt)).toFixed(2);
  const formCostAmount = parseFloat(paymentForm.CostAmount) || 0;
  const formCostTaxPer = parseFloat(paymentForm.CostTaxPer) || 0;
  const formCostTaxAmt = (formCostAmount * formCostTaxPer / 100).toFixed(2);
  const formTotalCost  = (formCostAmount + parseFloat(formCostTaxAmt)).toFixed(2);
  const formProfit     = (parseFloat(formTotalAmt) - parseFloat(formTotalCost)).toFixed(2);

  const handleAddPayment = () => {
    if (isInvoiced)       { setError('This consignment has already been invoiced — no new rows can be added.'); return; }
    if (!consignmentData) { setError('No consignment loaded'); return; }
    const amount = parseFloat(paymentForm.Amount) || 0, taxPer = parseFloat(paymentForm.TaxPer) || 0;
    const taxAmt = amount * (taxPer / 100), totalAmt = amount + taxAmt;
    const costAmount = parseFloat(paymentForm.CostAmount) || 0, costTaxPer = parseFloat(paymentForm.CostTaxPer) || 0;
    const costTaxAmt = costAmount * (costTaxPer / 100), totalCost = costAmount + costTaxAmt;
    const cust = customers.find(x => x.customerid == headerCustomerID);
    const rt   = revenueTypes.find(x => x.revenuetypeid == paymentForm.revenuetypeid);
    const supp = suppliers.find(x => x.supplierid == paymentForm.SupplierID);
    const newPayment = { ...paymentForm, CustomerID: headerCustomerID, Amount: amount.toFixed(2), TaxAmt: taxAmt.toFixed(2), TotalAmt: totalAmt.toFixed(2), CostAmount: costAmount.toFixed(2), CostTaxAmt: costTaxAmt.toFixed(2), TotalCost: totalCost.toFixed(2), CustomerName: cust?.customername || consignmentData?.InvoiceToName || '', revenuetypename: rt?.revenuetypename || '', SupplierName: supp?.suppliername || '' };
    if (editingPaymentIndex >= 0 && isEditingLocal) {
      const updated = [...localPayments]; updated[editingPaymentIndex] = newPayment; setLocalPayments(updated); setSuccess('Payment updated in list!');
    } else { setLocalPayments([...localPayments, newPayment]); setSuccess('Payment added — click Save All (F5) to commit to database.'); }
    setPaymentForm(getEmptyPaymentForm()); setEditingPaymentIndex(-1); setIsEditingLocal(false); setError('');
  };

  const handleEditLocalPayment = (index) => {
    if (isInvoiced) { setError('This consignment has already been invoiced — rows cannot be edited.'); return; }
    const p = localPayments[index];
    setPaymentForm({ revenuetypeid: p.revenuetypeid || '', Amount: p.Amount || '', TaxPer: p.TaxPer || 0, SupplierID: p.SupplierID || '', CostAmount: p.CostAmount || '', CostTaxPer: p.CostTaxPer || 0, Remarks: p.Remarks || '' });
    if (!headerCustomerLocked) setHeaderCustomerID(p.CustomerID || '');
    setEditingPaymentIndex(index); setIsEditingLocal(true); setError(''); setSuccess('');
  };

  const handleDeleteLocalPayment = (index) => {
    if (isInvoiced) { setError('This consignment has already been invoiced — rows cannot be removed.'); return; }
    if (!window.confirm('Remove this payment from the list?')) return;
    setLocalPayments(localPayments.filter((_, i) => i !== index)); setSuccess('Payment removed from list');
  };

  const handleEditSavedPayment = (p) => {
    if (isInvoiced) { setError('This consignment has already been invoiced — rows cannot be edited.'); return; }
    setPaymentForm({ revenuetypeid: p.revenuetypeid || '', Amount: p.Amount || '', TaxPer: p.TaxPer || 0, SupplierID: p.SupplierID || '', CostAmount: p.CostAmount || '', CostTaxPer: p.CostTaxPer || 0, Remarks: p.Remarks || '' });
    if (!headerCustomerLocked) setHeaderCustomerID(p.CustomerID || '');
    setEditingSavedPaymentId(p.ConsignmentPaymentID); setEditingPaymentIndex(-1); setIsEditingLocal(false); setError(''); setSuccess('');
  };

  const handleUpdateSavedPayment = async () => {
    if (isInvoiced) { setError('This consignment has already been invoiced — rows cannot be edited.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      await apiClient.put('/ConsignmentPaymentWithoutCurrency.php', { ConsignmentPaymentID: editingSavedPaymentId, CustomerID: headerCustomerID || null, revenuetypeid: paymentForm.revenuetypeid || null, Amount: paymentForm.Amount, TaxPer: paymentForm.TaxPer, SupplierID: paymentForm.SupplierID || null, CostAmount: paymentForm.CostAmount, CostTaxPer: paymentForm.CostTaxPer, Remarks: paymentForm.Remarks });
      setSuccess('Payment updated successfully!'); setPaymentForm(getEmptyPaymentForm()); setEditingSavedPaymentId(null); loadSavedPayments(consignmentData.ConsignmentID);
    } catch (err) { setError('Error updating payment: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const handleDeleteSavedPayment = async (p) => {
    if (isInvoiced) { setError('This consignment has already been invoiced — rows cannot be removed.'); return; }
    if (!window.confirm('Delete this saved payment? This cannot be undone.')) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      await apiClient.delete('/ConsignmentPaymentWithoutCurrency.php', { params: { id: p.ConsignmentPaymentID } });
      setSuccess('Payment deleted successfully!'); loadSavedPayments(consignmentData.ConsignmentID);
    } catch (err) { setError('Error deleting payment: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const cancelSavedEdit = () => { setPaymentForm(getEmptyPaymentForm()); setEditingSavedPaymentId(null); setError(''); setSuccess(''); };

  const handleSaveAll = async () => {
    if (isInvoiced)                 { setError('This consignment has already been invoiced — cannot save new rows.'); return; }
    if (!consignmentData)           { setError('No consignment loaded'); return; }
    if (localPayments.length === 0) { setError('No payments to save. Add payments first.'); return; }
    setLoading(true); setError(''); setSuccess('');
    let ok = 0, fail = 0, lastErrorMsg = '';
    for (const p of localPayments) {
      try {
        await apiClient.post('/ConsignmentPaymentWithoutCurrency.php', { ConsignmentID: consignmentData.ConsignmentID, CustomerID: p.CustomerID || null, revenuetypeid: p.revenuetypeid || null, Amount: p.Amount, TaxPer: p.TaxPer, SupplierID: p.SupplierID || null, CostAmount: p.CostAmount, CostTaxPer: p.CostTaxPer, Remarks: p.Remarks });
        ok++;
      } catch (err) { fail++; lastErrorMsg = err.response?.data?.message || err.message; }
    }
    if (ok > 0) { setSuccess(`${ok} payment(s) saved successfully!`); setLocalPayments([]); loadSavedPayments(consignmentData.ConsignmentID); handleSelect({ ConsignmentNo: consignmentData.ConsignmentNo }); }
    if (fail > 0) setError(`${fail} payment(s) failed to save.${lastErrorMsg ? ' ' + lastErrorMsg : ''}`);
    setLoading(false);
  };

  const handleClearAll = () => {
    setConsignmentData(null); setSavedPayments([]); setLocalPayments([]);
    setPaymentForm(getEmptyPaymentForm()); setHeaderCustomerID(''); setHeaderCustomerLocked(false);
    setEditingPaymentIndex(-1); setIsEditingLocal(false); setEditingSavedPaymentId(null);
    setError(''); setSuccess('');
  };

  const totals = (() => {
    const sRev  = savedPayments.reduce((s, p) => s + (parseFloat(p.TotalAmt)  || 0), 0);
    const sCost = savedPayments.reduce((s, p) => s + (parseFloat(p.TotalCost) || parseFloat(p.CostAmount) || 0), 0);
    const lRev  = localPayments.reduce((s, p) => s + (parseFloat(p.TotalAmt)  || 0), 0);
    const lCost = localPayments.reduce((s, p) => s + (parseFloat(p.TotalCost) || parseFloat(p.CostAmount) || 0), 0);
    const rev = sRev + lRev, cost = sCost + lCost;
    return { rev, cost, pl: rev - cost };
  })();

  const headerCustomerName = headerCustomerLocked
    ? consignmentData?.InvoiceToName
    : customers.find(c => c.customerid == headerCustomerID)?.customername || '';

  // shared class helpers
  const inpCls    = 'w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm bg-white outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)] font-[inherit] text-gray-900 box-border';
  const selCls    = inpCls + ' cursor-pointer';
  const lblCls    = 'block mb-1 text-[0.76rem] font-bold text-gray-700 uppercase tracking-wide';
  const secCardCls = 'bg-white border border-[#E2E8F0] rounded-xl p-5 mb-4';

  const SecHdr = ({ bg, color, icon: Icon, children }) => (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3.5 text-[0.87rem] font-bold"
      style={{ background: bg, color }}>
      {Icon && <Icon size={14} />}{children}
    </div>
  );

  const Alert = ({ type, children }) => (
    <div className="flex items-center gap-2 px-4 py-3 rounded-lg mb-3.5 text-[0.9rem] border"
      style={type === 'success'
        ? { background: '#DCFCE7', borderColor: '#86EFAC', color: '#15803D' }
        : { background: '#FEE2E2', borderColor: '#FCA5A5', color: '#B91C1C' }}
      >
      {children}
    </div>
  );

  const spinStyle = { width: 15, height: 15, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'cpf-spin 0.7s linear infinite' };

  return (
    <div className="px-6 pb-16 pt-5 box-border">
      <style>{`
        @keyframes cpf-spin   { to { transform: rotate(360deg); } }
        @keyframes cpf-fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* Toolbar */}
      <div className="flex gap-2.5 flex-wrap mb-5">
        <button type="button" onClick={handleSaveAll}
          disabled={loading || localPayments.length === 0 || isInvoiced}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#16A34A' }}
          onMouseEnter={(e) => { if (!loading && localPayments.length > 0 && !isInvoiced) e.currentTarget.style.backgroundColor = '#15803D'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#16A34A'; }}>
          {loading ? <><span style={spinStyle} />Saving…</> : <><Save size={15} />Save All ({localPayments.length}) (F5)</>}
        </button>
        <button type="button" onClick={handleClearAll} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#64748B' }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#475569'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#64748B'; }}>
          <RotateCcw size={15} /> Clear
        </button>
      </div>

      <div className="max-w-[1080px] mx-auto">

        {error   && <Alert type="error"><AlertCircle size={17} /><strong>Error:</strong> {error}</Alert>}
        {success && <Alert type="success"><CheckCircle size={17} /><strong>Success:</strong> {success}</Alert>}

        {isInvoiced && (
          <Alert type="error"><Lock size={17} /><strong>Invoiced:</strong> This consignment has already been invoiced. Payment rows are locked and cannot be added, edited, or deleted.</Alert>
        )}

        {/* Search section */}
        <div className={secCardCls}>
          <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-800 mb-3.5 pb-3 border-b-2 border-gray-100">
            <Search size={17} style={{ color: PRIMARY }} /> Search Consignment
          </div>
          {consignmentData ? (
            <SelectedBar data={consignmentData} onClear={handleClearAll} />
          ) : (
            <>
              <ConsignmentSearchBox onSelect={handleSelect} disabled={loading} />
              <p className="mt-2 text-[0.77rem] text-gray-400">
                Type consignment number, shipper, or consignee — results appear instantly. Use ↑↓ &amp; Enter to select.
              </p>
            </>
          )}
        </div>

        {/* Consignment Details */}
        {consignmentData && (
          <div className="rounded-xl p-5 mb-4" style={{ background: '#EFF6FF', border: '1px solid #93C5FD' }}>
            <div className="flex items-center gap-2 text-[0.95rem] font-semibold mb-3.5 pb-3 border-b-2" style={{ color: '#1D4ED8', borderColor: '#93C5FD' }}>
              <Package size={17} /> Consignment Details
            </div>
            <div className="grid gap-3.5 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))' }}>
              {[
                { l: 'Consignment No', v: consignmentData.ConsignmentNo, mono: true },
                { l: 'Date',        v: new Date(consignmentData.ConsignmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
                { l: 'Shipper',     v: consignmentData.ShipperName   || '-' },
                { l: 'Consignee',   v: consignmentData.ConsigneeName || '-' },
                { l: 'Origin',      v: consignmentData.OriginCity    || consignmentData.OriginCountry    || 'N/A' },
                { l: 'Destination', v: consignmentData.DestinationCity || consignmentData.DestinationCountry || 'N/A' },
                { l: 'Pieces',      v: consignmentData.Pieces || 0 },
                { l: 'Weight',      v: `${consignmentData.Weight || consignmentData.StatedWeight || 0} kg` },
              ].map(x => (
                <div key={x.l} className="flex flex-col gap-0.5">
                  <label className="text-[0.69rem] font-bold uppercase tracking-wide" style={{ color: PRIMARY }}>{x.l}</label>
                  <div className={`font-semibold text-[0.87rem] px-2 py-1.5 bg-white/70 rounded-md ${x.mono ? 'font-mono' : ''}`} style={{ color: '#1D4ED8' }}>{x.v}</div>
                </div>
              ))}
            </div>

            {/* Summary strip */}
            <div className="flex border border-gray-200 rounded-lg overflow-hidden mt-1">
              {[
                { label: 'Saved',   value: savedPayments.length, color: '#1F2937' },
                { label: 'Pending', value: localPayments.length, color: '#F59E0B' },
                { label: 'Revenue', value: totals.rev.toFixed(2), color: '#16A34A' },
                { label: 'Cost',    value: totals.cost.toFixed(2), color: '#DC2626' },
                { label: 'P / L',   value: totals.pl.toFixed(2), color: totals.pl >= 0 ? '#16A34A' : '#DC2626' },
                ...(isInvoiced ? [{ label: 'Status', value: null, isInvoiced: true }] : []),
              ].map((item, i, arr) => (
                <div key={item.label} className={`flex flex-col items-center py-2.5 px-5 flex-1 bg-gray-50 ${i < arr.length - 1 ? 'border-r border-gray-200' : ''}`}>
                  <span className="text-[0.68rem] font-bold text-gray-500 uppercase tracking-widest mb-1">{item.label}</span>
                  {item.isInvoiced
                    ? <span className="font-bold flex items-center gap-1 text-[1.1rem]" style={{ color: '#DC2626' }}><Lock size={13} />Invoiced</span>
                    : <span className="font-bold text-[1.1rem]" style={{ color: item.color }}>{item.value}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {consignmentData && (<>

          {/* Header: Customer */}
          <div className={secCardCls}>
            <SecHdr bg="#e0f2fe" color="#0c4a6e" icon={User}>
              Invoice / Payment Header
              <span className="ml-auto text-[0.75rem] font-normal" style={{ color: PRIMARY }}>These apply to every row added below</span>
            </SecHdr>
            <div>
              <label className={lblCls}>
                Customer
                {headerCustomerLocked && <span className="ml-2 text-[0.75rem] font-normal" style={{ color: '#16A34A' }}>✓ auto from Invoice To</span>}
              </label>
              {headerCustomerLocked ? (
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[0.92rem]" style={{ background: '#DCFCE7', border: '2px solid #86EFAC', color: '#15803D' }}>
                  <User size={15} className="flex-shrink-0" style={{ color: '#16A34A' }} />
                  <div>
                    <div className="font-semibold">{consignmentData.InvoiceToName}</div>
                    {consignmentData.InvoiceToCode && <div className="text-[0.75rem] text-gray-500 font-mono">{consignmentData.InvoiceToCode}</div>}
                  </div>
                  {!isInvoiced && (
                    <button type="button" onClick={() => { setHeaderCustomerLocked(false); setHeaderCustomerID(''); }}
                      className="ml-auto bg-transparent border-0 cursor-pointer text-gray-400 text-[0.75rem] flex items-center gap-0.5 hover:text-gray-600" title="Override customer">
                      <X size={12} /> Override
                    </button>
                  )}
                </div>
              ) : (
                <select value={headerCustomerID} onChange={e => setHeaderCustomerID(e.target.value)} disabled={isInvoiced} className={selCls}>
                  <option value="">-- Select Customer --</option>
                  {customers.map(c => <option key={c.customerid} value={c.customerid}>{c.customername}</option>)}
                </select>
              )}
            </div>
          </div>

          {/* Row entry form */}
          {!isInvoiced && (
          <div className={secCardCls}>
            <SecHdr bg="#dbeafe" color="#1e3a5f" icon={Calculator}>
              {editingSavedPaymentId ? 'Edit Saved Payment Row' : editingPaymentIndex >= 0 ? 'Edit Payment Row' : 'Add Payment Row'}
            </SecHdr>

            <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div>
                <label className={lblCls}>Revenue Type</label>
                <select name="revenuetypeid" value={paymentForm.revenuetypeid} onChange={handlePaymentFormChange} className={selCls}>
                  <option value="">-- Select Revenue Type --</option>
                  {revenueTypes.filter(rt => parseInt(rt.isactive) === 1).map(rt => (
                    <option key={rt.revenuetypeid} value={rt.revenuetypeid}>{rt.revenuetypecode || '?'} — {rt.revenuetypename || '(no name)'}</option>
                  ))}
                </select>
              </div>
            </div>

            <SecHdr bg="#fef3c7" color="#78350f" icon={TrendingUp}>Revenue Details</SecHdr>
            <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Revenue Amount</label><input type="number" step="0.01" name="Amount" value={paymentForm.Amount} onChange={handlePaymentFormChange} placeholder="0.00" className={inpCls} /></div>
              <div><label className={lblCls}>Tax %</label><input type="number" step="0.01" name="TaxPer" value={paymentForm.TaxPer} onChange={handlePaymentFormChange} placeholder="0.00" className={inpCls} /></div>
              <div><label className={lblCls}>Tax Amount (Auto)</label><input type="number" step="0.01" value={formTaxAmt} readOnly className={`${inpCls} bg-gray-100 cursor-not-allowed text-gray-500`} /></div>
              <div><label className={lblCls}>Total Revenue (Auto)</label><input type="number" step="0.01" value={formTotalAmt} readOnly className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm outline-none font-[inherit] box-border font-bold cursor-not-allowed" style={{ background: '#EFF6FF', color: '#1D4ED8' }} /></div>
            </div>

            <SecHdr bg="#d1fae5" color="#064e3b" icon={DollarSign}>Cost Details</SecHdr>
            <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))' }}>
              <div><label className={lblCls}>Supplier</label>
                <select name="SupplierID" value={paymentForm.SupplierID} onChange={handlePaymentFormChange} className={selCls}>
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map(s => <option key={s.supplierid} value={s.supplierid}>{s.suppliername}</option>)}
                </select>
              </div>
              <div><label className={lblCls}>Cost Amount</label><input type="number" step="0.01" name="CostAmount" value={paymentForm.CostAmount} onChange={handlePaymentFormChange} placeholder="0.00" className={inpCls} /></div>
              <div><label className={lblCls}>Cost Tax %</label><input type="number" step="0.01" name="CostTaxPer" value={paymentForm.CostTaxPer} onChange={handlePaymentFormChange} placeholder="0.00" className={inpCls} /></div>
              <div><label className={lblCls}>Cost Tax Amount (Auto)</label><input type="number" step="0.01" value={formCostTaxAmt} readOnly className={`${inpCls} bg-gray-100 cursor-not-allowed text-gray-500`} /></div>
              <div><label className={lblCls}>Total Cost (Auto)</label><input type="number" step="0.01" value={formTotalCost} readOnly className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm outline-none font-[inherit] box-border font-bold cursor-not-allowed" style={{ background: '#DCFCE7', color: '#15803D' }} /></div>
              <div><label className={lblCls}>Profit / Loss (Auto)</label>
                <input type="number" step="0.01" value={formProfit} readOnly
                  className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm outline-none font-[inherit] box-border font-bold cursor-not-allowed"
                  style={parseFloat(formProfit) >= 0 ? { background: '#DCFCE7', color: '#15803D' } : { background: '#FEE2E2', color: '#B91C1C' }} />
              </div>
            </div>

            <div className="mb-4">
              <label className={lblCls}>Remarks</label>
              <textarea name="Remarks" value={paymentForm.Remarks} onChange={handlePaymentFormChange} rows={2}
                placeholder="Payment remarks" className={`${inpCls} resize-y min-h-[64px]`} />
            </div>

            <div className="flex gap-3 flex-wrap pt-4 border-t border-gray-200">
              <button type="button" onClick={editingSavedPaymentId ? handleUpdateSavedPayment : handleAddPayment} disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white border-0 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                style={{ backgroundColor: PRIMARY }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = PRIMARY_DARK; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}>
                {editingSavedPaymentId
                  ? <><Save size={16} />{loading ? 'Updating…' : 'Update Saved Row'}</>
                  : editingPaymentIndex >= 0 ? <><Save size={16} />Update Row</> : <><Plus size={16} />Add Row to List</>}
              </button>
              {editingSavedPaymentId && (
                <button type="button" onClick={cancelSavedEdit} disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-[#CBD5E1] rounded-lg cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150">
                  Cancel Edit
                </button>
              )}
              {editingPaymentIndex >= 0 && !editingSavedPaymentId && (
                <button type="button" onClick={() => { setPaymentForm(getEmptyPaymentForm()); setEditingPaymentIndex(-1); setIsEditingLocal(false); }}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-[#CBD5E1] rounded-lg cursor-pointer hover:bg-gray-50 transition-all duration-150">
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
          )}

          {/* Payment table */}
          {(savedPayments.length > 0 || localPayments.length > 0) && (
            <div className={secCardCls}>
              <SecHdr bg="#EFF6FF" color="#1D4ED8" icon={TrendingUp}>
                Payment Entries ({savedPayments.length} saved, {localPayments.length} pending)
                {headerCustomerName && (
                  <span className="ml-auto text-[0.76rem] font-normal text-gray-500">Customer: <strong>{headerCustomerName}</strong></span>
                )}
              </SecHdr>

              <div className="overflow-x-auto border border-gray-200 rounded-lg mb-3 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded">
                <table className="w-full border-collapse text-[0.83rem]">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Status','Revenue Type','Supplier','Revenue','Rev Tax','Total Rev','Cost','Cost Tax','Total Cost','P / L','Actions'].map(h => (
                        <th key={h} className={`px-3 py-2.5 border-b-2 border-gray-200 text-[0.72rem] font-bold uppercase tracking-wide text-gray-600 whitespace-nowrap ${['Revenue','Rev Tax','Total Rev','Cost','Cost Tax','Total Cost','P / L'].includes(h) ? 'text-right' : h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {savedPayments.map(p => {
                      const totalCost = parseFloat(p.TotalCost || 0) || (parseFloat(p.CostAmount || 0) + parseFloat(p.CostTaxAmt || 0));
                      const pl = parseFloat(p.TotalAmt || 0) - totalCost;
                      return (
                        <tr key={`s-${p.ConsignmentPaymentID}`} className="transition-colors duration-75 border-b border-gray-100" style={{ background: '#F0FDF4' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#DCFCE7'} onMouseLeave={(e) => e.currentTarget.style.background = '#F0FDF4'}>
                          <td className="px-3 py-2.5"><span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.72rem] font-bold uppercase tracking-wide whitespace-nowrap" style={{ background: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC' }}><CheckCircle size={12} />Saved</span></td>
                          <td className="px-3 py-2.5">{p.revenuetypename || '-'}</td>
                          <td className="px-3 py-2.5">{p.SupplierName || '-'}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.Amount||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.TaxAmt||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-bold">{parseFloat(p.TotalAmt||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.CostAmount||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.CostTaxAmt||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-bold">{totalCost.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-bold" style={{ color: pl >= 0 ? '#16A34A' : '#DC2626' }}>{pl.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-center">
                            {p.IsInvoiced ? (
                              <span className="text-[0.75rem] flex items-center justify-center gap-1" style={{ color: '#DC2626' }}><Lock size={12} />Invoiced</span>
                            ) : (
                              <div className="flex gap-1.5 justify-center">
                                <button type="button" title="Edit" onClick={() => handleEditSavedPayment(p)} disabled={loading} className="p-1 bg-white rounded-md cursor-pointer disabled:opacity-50 flex items-center" style={{ border: '1px solid #93C5FD', color: PRIMARY }}><Edit2 size={14} /></button>
                                <button type="button" title="Delete" onClick={() => handleDeleteSavedPayment(p)} disabled={loading} className="p-1 bg-white rounded-md cursor-pointer disabled:opacity-50 flex items-center" style={{ border: '1px solid #FCA5A5', color: '#DC2626' }}><Trash2 size={14} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {localPayments.map((p, i) => {
                      const totalCost = parseFloat(p.TotalCost || 0) || (parseFloat(p.CostAmount || 0) + parseFloat(p.CostTaxAmt || 0));
                      const pl = parseFloat(p.TotalAmt || 0) - totalCost;
                      return (
                        <tr key={`l-${i}`} className="transition-colors duration-75 border-b border-gray-100" style={{ background: '#FFFBEB' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#FEF3C7'} onMouseLeave={(e) => e.currentTarget.style.background = '#FFFBEB'}>
                          <td className="px-3 py-2.5"><span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.72rem] font-bold uppercase tracking-wide whitespace-nowrap" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FCD34D' }}><AlertCircle size={12} />Pending</span></td>
                          <td className="px-3 py-2.5">{p.revenuetypename || '-'}</td>
                          <td className="px-3 py-2.5">{p.SupplierName || '-'}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.Amount||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.TaxAmt||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-bold">{parseFloat(p.TotalAmt||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.CostAmount||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right">{parseFloat(p.CostTaxAmt||0).toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-bold">{totalCost.toFixed(2)}</td>
                          <td className="px-3 py-2.5 text-right font-bold" style={{ color: pl >= 0 ? '#16A34A' : '#DC2626' }}>{pl.toFixed(2)}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex gap-1.5 justify-center">
                              <button type="button" title="Edit" onClick={() => handleEditLocalPayment(i)} disabled={isInvoiced} className="p-1 bg-white rounded-md cursor-pointer disabled:opacity-50 flex items-center" style={{ border: '1px solid #93C5FD', color: PRIMARY }}><Edit2 size={14} /></button>
                              <button type="button" title="Delete" onClick={() => handleDeleteLocalPayment(i)} disabled={isInvoiced} className="p-1 bg-white rounded-md cursor-pointer disabled:opacity-50 flex items-center" style={{ border: '1px solid #FCA5A5', color: '#DC2626' }}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr className="border-t-2 border-gray-300">
                      <td colSpan="5" className="px-3 py-3 text-right font-bold">Totals:</td>
                      <td className="px-3 py-3 text-right font-bold">{totals.rev.toFixed(2)}</td>
                      <td colSpan="2" />
                      <td className="px-3 py-3 text-right font-bold">{totals.cost.toFixed(2)}</td>
                      <td className="px-3 py-3 text-right font-bold" style={{ color: totals.pl >= 0 ? '#16A34A' : '#DC2626' }}>{totals.pl.toFixed(2)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {localPayments.length > 0 && !isInvoiced && (
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg text-[0.87rem] font-medium mt-3" style={{ background: '#FFFBEB', border: '2px solid #F59E0B', color: '#B45309' }}>
                  <AlertCircle size={18} />
                  <span>You have {localPayments.length} unsaved payment(s). Click <strong className="bg-white/50 px-1 rounded">"Save All (F5)"</strong> to commit to database.</span>
                </div>
              )}
            </div>
          )}

        </>)}
      </div>
    </div>
  );
};

export default ConsignmentPaymentWithoutCurrency;